/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Offcanvas, Overlay, Tooltip } from "react-bootstrap";
import { faCog } from "@fortawesome/free-solid-svg-icons";
import InputLabel from "./InputLabel";
import HelpButton from "../../../../HelpContent/HelpButton";
import SelectInput, { SelectInputProps } from "./SelectInput";
import TextAreaInput, { TextAreaInputProps } from "./TextAreaInput";
import CheckboxInput, { CheckboxInputProps } from "./CheckboxInput";
import RadioButtonInput, { RadioButtonInputProps } from "./RadioButtonInput";
import TabInput, { TabInputProps } from "./TabInput";
import PhoneInputComponent, {
  PhoneInputComponentProps,
} from "./PhoneInputComponent";
import DateTimeInput, {
  DateTimeInputProps,
  DateInterval,
} from "./DateTimeInput";
import TextInput, { TextInputProps } from "./TextInput";
import MapInput, { MapInputProps } from "./MapInput";
import ButtonInput, { ButtonInputProps } from "./ButtonInput";
import ContactInput, { ContactInputProps } from "./ContactInput";
import ContactSetInput, { ContactSetInputProps } from "./ContactSetInput";
import AssetInput, { AssetInputProps } from "./AssetInput";
import BusinessHoursForm, {
  getDefaultBusinessHours,
} from "../../../Components/BusinessHoursForm";

export const validateEmail = (email: string) =>
  String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

interface InputHelp {
  id?: string;
  [key: string]: any;
}

export type InputHelpType = InputHelp | string | "";

// Re-export individual input interfaces so consumers can import them from Input.tsx
export type {
  SelectInputProps,
  TextAreaInputProps,
  CheckboxInputProps,
  RadioButtonInputProps,
  ButtonInputProps,
  TabInputProps,
  PhoneInputComponentProps,
  DateTimeInputProps,
  DateInterval,
  TextInputProps,
  MapInputProps,
  ContactInputProps,
  ContactSetInputProps,
  AssetInputProps,
};

// ---------------------------------------------------------------------------
// Shared base — props present on every input variant (owned by Input.tsx)
// ---------------------------------------------------------------------------
interface BaseInputProps {
  id?: string;
  value: any[];
  onChange: (values: any[]) => void;
  inputHelp?: InputHelpType;
  label?: string;
  isDisabled?: boolean;
  isSummary?: boolean;
  configuration?: React.ReactNode | null;
  html?: React.ReactNode;
  isInput?: boolean;
  className?: string;
  placeholder?: string;
  modalAsOffcanvas?: boolean;
}

// ---------------------------------------------------------------------------
// Wrapper variants — extend the individual file's interface with base props
// ---------------------------------------------------------------------------

type WrappedSelectInputProps = Omit<SelectInputProps, "value" | "onChange"> &
  BaseInputProps & { inputType: "select" };

type WrappedTextAreaInputProps = Omit<
  TextAreaInputProps,
  "value" | "onChange"
> &
  BaseInputProps & { inputType: "textArea" };

type WrappedCheckboxInputProps = Omit<
  CheckboxInputProps,
  "value" | "onChange"
> &
  BaseInputProps & { inputType: "checkbox" };

type WrappedRadioInputProps = Omit<
  RadioButtonInputProps,
  "value" | "onChange"
> &
  BaseInputProps & { inputType: "radio" };

type WrappedButtonInputProps = Omit<ButtonInputProps, "value" | "onChange"> &
  BaseInputProps & { inputType: "button" };

type WrappedTabInputProps = Omit<TabInputProps, "value" | "onChange"> &
  BaseInputProps & { inputType: "tab" };

type WrappedPhoneInputProps = Omit<
  PhoneInputComponentProps,
  "value" | "onChange"
> &
  BaseInputProps & { inputType: "phone" };

type WrappedDateTimeInputProps = Omit<
  DateTimeInputProps,
  "value" | "onChange"
> &
  BaseInputProps & { inputType: "dateTime" };

type WrappedTextInputProps = Omit<TextInputProps, "value" | "onChange"> &
  BaseInputProps & {
    inputType?: "text" | null;
    isEmail?: boolean;
    isMulti?: boolean;
    isClearable?: boolean;
    loadOptions?: ((search: string, prevOptions: any[]) => Promise<any>) | null;
    getValueLabels?: ((values: any[]) => Promise<any[]>) | null;
    options?: any[];
    onCreateOption?: ((v: any) => void) | null;
    isOptionDisabled?: (o: any) => boolean;
    isValidNewOption?: (a: any) => boolean;
    autoFocus?: boolean;
    components?: any;
    isSearchable?: boolean;
    isContactSet?: boolean;
    formatOptionLabel?: (o: any) => any;
    filterOption?: ((option: any, inputValue: string) => boolean) | undefined;
  };

type WrappedMapInputProps = Omit<MapInputProps, "values" | "onChange"> &
  BaseInputProps & {
    inputType: "map";
  };

type WrappedContactInputProps = Omit<ContactInputProps, "values" | "onChange"> &
  BaseInputProps & {
    inputType: "contact";
    modalAsOffcanvas?: boolean;
  };

type WrappedContactSetInputProps = Omit<
  ContactSetInputProps,
  "values" | "onChange"
> &
  BaseInputProps & {
    inputType: "contactSet";
    modalAsOffcanvas?: boolean;
  };

type WrappedAssetInputProps = Omit<AssetInputProps, "values" | "onChange"> &
  BaseInputProps & {
    inputType: "asset";
  };

type WrappedBusinessHoursInputProps = BaseInputProps & {
  inputType: "businessHours";
};

// ---------------------------------------------------------------------------
// Union — InputProps is any one of the wrapped variants
// ---------------------------------------------------------------------------
export type InputProps =
  | WrappedSelectInputProps
  | WrappedTextAreaInputProps
  | WrappedCheckboxInputProps
  | WrappedRadioInputProps
  | WrappedButtonInputProps
  | WrappedTabInputProps
  | WrappedPhoneInputProps
  | WrappedDateTimeInputProps
  | WrappedTextInputProps
  | WrappedMapInputProps
  | WrappedContactInputProps
  | WrappedContactSetInputProps
  | WrappedAssetInputProps
  | WrappedBusinessHoursInputProps;

/**
 * Props for Input config objects (e.g. in actionOptionsConfig for "option" and "property" type inputs).
 * Same as InputProps but without `value` and `onChange`, which are
 * provided at runtime by the rendering layer (CommonInput).
 */
export type InputConfigProps = Omit<InputProps, "value" | "onChange">;

export default function Input(props: InputProps) {
  const {
    value,
    onChange,
    inputHelp = "",
    label = "",
    isDisabled = false,
    isSummary = false,
    configuration = null,
    id = undefined,
    placeholder = "",
    html = "",
    isInput = true,
    className = "",
    inputType,
  } = props;

  // Variant-specific props accessed via cast — safe because `inputType` guards usage
  const p = props as any;
  const options = p.options;
  const isMulti = p.isMulti ?? false;
  const isMultiple = p.isMultiple ?? false;
  const isClearable = p.isClearable ?? false;
  const onKeyUp = p.onKeyUp ?? null;
  const onCreateOption = p.onCreateOption ?? null;
  const type = p.type ?? null;
  const isOptionDisabled = p.isOptionDisabled ?? (() => false);
  const min = p.min ?? null;
  const max = p.max ?? null;
  const isValidNewOption = p.isValidNewOption ?? (() => false);
  const loadOptions = p.loadOptions ?? null;
  const getValueLabels = p.getValueLabels ?? null;
  const autoFocus = p.autoFocus ?? false;
  const maxLength = p.maxLength ?? null;
  const textAreaHeight = p.textAreaHeight;
  const radioName = p.radioName ?? "radio";
  const isEmail = p.isEmail ?? false;
  const dateTimeType = p.dateTimeType ?? "";
  const maxDate = p.maxDate ?? null;
  const minDate = p.minDate ?? null;
  const excludeDates = p.excludeDates;
  const excludeDateIntervals = p.excludeDateIntervals;
  const isDateTimeUTC = p.isDateTimeUTC ?? true;
  const components = p.components ?? null;
  const isSearchable = p.isSearchable ?? true;
  const isContactSet = p.isContactSet ?? false;
  const onCountryChange = p.onCountryChange;
  const helper = p.helper;
  const formatOptionLabel = p.formatOptionLabel ?? ((o: any) => o.label);
  const filterOption = p.filterOption;
  const mapDrawingModes = p.mapDrawingModes;
  const modalAsOffcanvas = p.modalAsOffcanvas ?? false;

  const [showConfig, setShowConfig] = useState(false);
  const target = useRef<HTMLDivElement>(null);

  const errors =
    (isEmail && !validateEmail(value[0]) && "Email not valid") ||
    (maxLength &&
      value[0]?.length === maxLength &&
      `Maximum ${maxLength} characters allowed`);
  const popover = <Tooltip>{errors}</Tooltip>;

  const renderInput = () => {
    if (inputType === "businessHours") {
      let businessHoursValue = getDefaultBusinessHours();
      if (value[0]) {
        try {
          businessHoursValue = {
            ...businessHoursValue,
            ...(typeof value[0] === "string" ? JSON.parse(value[0]) : value[0]),
          };
        } catch {
          businessHoursValue = getDefaultBusinessHours();
        }
      }

      return (
        <BusinessHoursForm
          value={businessHoursValue}
          onChange={(businessHours: Record<string, unknown>) =>
            onChange([JSON.stringify(businessHours)])
          }
        />
      );
    }

    if (inputType === "contact") {
      return (
        <ContactInput
          values={value}
          onChange={onChange}
          isMultiple={isMultiple}
          hideList={p.hideList}
          modalAsOffcanvas={modalAsOffcanvas}
        />
      );
    }

    if (inputType === "contactSet") {
      return (
        <ContactSetInput
          values={value}
          onChange={onChange}
          isMultiple={isMultiple}
          modalAsOffcanvas={modalAsOffcanvas}
        />
      );
    }

    if (inputType === "asset") {
      return (
        <AssetInput
          values={value}
          onChange={onChange}
          isMultiple={isMultiple}
          hideList={p.hideList}
        />
      );
    }

    if (
      inputType === "select" ||
      typeof loadOptions === "function" ||
      isMulti
    ) {
      return (
        <SelectInput
          key={options?.map((o: any) => o.value).join(",") || "select"} // reset when options change
          value={value}
          onChange={onChange}
          options={options}
          isMulti={isMulti}
          isDisabled={isDisabled}
          isSummary={isSummary}
          isClearable={isClearable}
          isSelect={typeof loadOptions === "function" || inputType === "select"}
          onCreateOption={onCreateOption}
          isOptionDisabled={isOptionDisabled}
          isValidNewOption={isValidNewOption}
          loadOptions={loadOptions}
          getValueLabels={getValueLabels}
          autoFocus={autoFocus}
          placeholder={placeholder}
          components={components}
          isSearchable={isSearchable}
          isContactSet={isContactSet}
          formatOptionLabel={formatOptionLabel}
          filterOption={filterOption}
          maxLength={maxLength}
        />
      );
    }

    if (inputType === "textArea") {
      return (
        <TextAreaInput
          value={value}
          onChange={onChange}
          isDisabled={isDisabled}
          isSummary={isSummary}
          onKeyUp={
            onKeyUp as React.KeyboardEventHandler<HTMLTextAreaElement> | null
          }
          placeholder={placeholder}
          maxLength={maxLength}
          textAreaHeight={textAreaHeight}
        />
      );
    }

    if (inputType === "checkbox") {
      return (
        <CheckboxInput
          value={value}
          onChange={onChange}
          options={options!}
          label={label}
          isDisabled={isDisabled}
        />
      );
    }

    if (inputType === "radio") {
      return (
        <RadioButtonInput
          value={value}
          onChange={onChange}
          options={options!}
          isDisabled={isDisabled}
          isSummary={isSummary}
          radioName={radioName}
        />
      );
    }

    if (inputType === "button") {
      return (
        <ButtonInput
          value={value}
          onChange={onChange}
          options={options!}
          isDisabled={isDisabled}
          isSummary={isSummary}
          radioName={radioName}
          isMultiple={isMultiple}
        />
      );
    }

    if (inputType === "tab") {
      return <TabInput value={value} onChange={onChange} options={options!} />;
    }

    if (inputType === "phone") {
      return (
        <PhoneInputComponent
          value={value}
          onChange={onChange}
          onCountryChange={onCountryChange}
          helper={helper}
          placeholder={placeholder}
        />
      );
    }

    if (inputType === "dateTime") {
      return (
        <DateTimeInput
          id={id}
          value={value}
          onChange={onChange}
          isSummary={isSummary}
          isClearable={isClearable}
          dateTimeType={dateTimeType}
          maxDate={maxDate}
          minDate={minDate}
          excludeDates={excludeDates}
          excludeDateIntervals={excludeDateIntervals}
          isDateTimeUTC={isDateTimeUTC}
          placeholder={placeholder}
        />
      );
    }

    if (inputType === "map") {
      return (
        <MapInput
          isMultiple={isMultiple}
          mapDrawingModes={mapDrawingModes}
          values={value}
          onChange={onChange}
        />
      );
    }

    return (
      <TextInput
        value={value}
        onChange={onChange}
        isDisabled={isDisabled}
        isSummary={isSummary}
        onKeyUp={onKeyUp as React.KeyboardEventHandler<HTMLInputElement> | null}
        type={type}
        min={min}
        max={max}
        placeholder={placeholder}
        maxLength={maxLength}
      />
    );
  };

  return (
    <div className="boom-input">
      {errors && !isSummary && (
        <Overlay show target={target.current}>
          {popover}
        </Overlay>
      )}
      {isInput && (
        <div className={`${className}`}>
          {label && !isSummary && inputType !== "checkbox" && (
            <InputLabel>{label}</InputLabel>
          )}

          <div className="align-bottom d-flex" ref={target}>
            <div className="flex-grow-1">{renderInput()}</div>
            {(inputHelp as InputHelp)?.id && (
              <HelpButton {...(inputHelp as any)} />
            )}
          </div>
        </div>
      )}

      {configuration && (
        <>
          <button
            type="button"
            className="btn btn-outline-secondary border-secondary-subtle border-start-0"
            onClick={() => setShowConfig(true)}
          >
            <FontAwesomeIcon icon={faCog} />
          </button>
          <Offcanvas
            show={showConfig}
            placement="end"
            onHide={() => {
              setShowConfig(false);
            }}
          >
            <Offcanvas.Header>
              <Offcanvas.Title>
                <FontAwesomeIcon icon={faCog} /> Input configuration options
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>{configuration}</Offcanvas.Body>
          </Offcanvas>
        </>
      )}

      {html}
    </div>
  );
}
